import { useEffect } from 'react';
import { Collapse, TextField, Alert, Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ErrorOutline } from '@mui/icons-material';
import { LoadingButton } from 'components';
import useValidationSchema from './services/useValidationSchema';
import useResetRequest from './services/useResetRequest';

const ResetPasswordForm = ({ onSuccess }: { onSuccess: () => void }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const emailSchema = useValidationSchema();
  const { response, loading, error, resetPasswordRequest } = useResetRequest();
  const { register, handleSubmit, formState } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(emailSchema),
  });
  const { errors } = formState;

  // @ts-ignore
  const onSubmit = (data) => resetPasswordRequest(data);

  useEffect(() => {
    if (response) {
      onSuccess();
    }
  }, [response, onSuccess]);

  return (
    <Box
      component="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Collapse in={!!error}>
        <Alert
          variant="outlined"
          severity="error"
          color="error"
          sx={{
            marginBottom: theme.spacing(4),
            [theme.breakpoints.down('sm')]: {
              color: theme.palette.grey[50],
              fontSize: 12,
            },
          }}
          data-testid="resetPasswordErrorAlert"
          icon={<ErrorOutline color="error" />}
        >
          {error && error.message}
        </Alert>
      </Collapse>
      <TextField
        sx={{ minHeight: theme.spacing(11.25) }}
        fullWidth
        id="email"
        error={!!errors.email}
        helperText={errors.email && errors.email.message}
        {...register('email')}
        variant="outlined"
        label={t('fields:email')}
        data-testid="email"
      />
      <LoadingButton
        type="submit"
        variant="rounded"
        fullWidth
        sx={{
          marginBottom: theme.spacing(5),
          height: theme.spacing(5),
        }}
        disabled={!formState.isValid}
        loading={loading}
        data-testid="submit-button"
      >
        {t('auth:reset')}
      </LoadingButton>
    </Box>
  );
};

export default ResetPasswordForm;
